import React, { useMemo } from 'react'

import { ContainsEditableProps, EditableType } from '@toasttab/sites-components'
import classnames from 'classnames'
import { Maybe } from 'graphql/jsutils/Maybe'

import { DoMenus_GetMenuItemsItem as DoMenusGetMenuItem } from 'src/apollo/onlineOrdering'

import LinkedItem from './LinkedItem'
import { GroupItemBackupContent, MenuGroupBlockConfig } from './types'
import {
  getBorderStyle,
  getBoxShadowStyle,
  getBorderRadiusStyle
} from 'src/shared/utils/stylingUtils'
import { BorderAndShadowConfig } from 'src/apollo/sites'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

type TestProps = { testId?: string }

export type GroupedMenuItemsProps = {
  classNames?: string
  config: MenuGroupBlockConfig | null
  styles?: any
  name: string
  description: string
  editableRef: EditableType<HTMLDivElement>
  itemsToRender: GroupItemBackupContent[] | Maybe<DoMenusGetMenuItem>[]
  groupGuid?: string | undefined
  borderAndShadowConfig?: BorderAndShadowConfig | null
  overrideGlobalBorderAndShadowConfig?: boolean | null
} & TestProps &
  ContainsEditableProps

/*
 * Renders a list of menu items as a MenuGroup, regardless if items are related to each other.
 * Used in the MenuGroup, to be used in FeaturedItems.
 */
const GroupedMenuItems = (props: GroupedMenuItemsProps) => {
  const {
    editableRef,
    config,
    styles = {},
    itemsToRender,
    name,
    description,
    borderAndShadowConfig,
    overrideGlobalBorderAndShadowConfig
  } = props

  const { restaurant } = useRestaurant()
  const designConfig = restaurant?.config?.designConfig?.borderAndShadowConfig

  const compositeConfig = useMemo(() => {
    const blockScopedConfig =
      borderAndShadowConfig ?? config?.appearance?.borderOptions

    // generally, overrideGlobalBorderAndShadowConfig = null should be treated as true for backward compatibility
    // unless block is new and doesnt have any config
    if (overrideGlobalBorderAndShadowConfig === null && !blockScopedConfig) {
      return designConfig
    }

    if (overrideGlobalBorderAndShadowConfig === false) {
      return designConfig
    }

    return blockScopedConfig
  }, [
    designConfig,
    borderAndShadowConfig,
    overrideGlobalBorderAndShadowConfig,
    config?.appearance?.borderOptions
  ])

  const columnsClass = useMemo(() => {
    const numColumns = config?.appearance?.columns || 1
    return `columns${numColumns}`
  }, [config?.appearance?.columns])

  const dividerStyle = useMemo(() => {
    if (config?.appearance?.dividerOptions?.showDivider === false) {
      return {}
    }

    const { dividerColor, dividerWidth, dividerStyle } =
      config?.appearance.dividerOptions || {}

    const color = `${dividerColor || 'var(--ds-border-default)'}`
    const style = dividerStyle || 'solid'
    const borderBottomWidth = `${dividerWidth ?? 1}px`

    return { borderBottom: `${style} ${color}`, borderBottomWidth }
  }, [config?.appearance.dividerOptions])

  const borderStyle = useMemo(() => {
    return getBorderStyle(compositeConfig)
  }, [compositeConfig])

  const boxShadowStyle = useMemo(() => {
    return getBoxShadowStyle(compositeConfig)
  }, [compositeConfig])

  const borderRadiusStyle = useMemo(() => {
    return getBorderRadiusStyle(compositeConfig, 16)
  }, [compositeConfig])

  return (
    <div className='editableMenuGroupContainer' ref={editableRef}>
      <div
        className='groupContainer'
        style={{
          ...styles,
          ...borderStyle,
          ...boxShadowStyle,
          ...borderRadiusStyle
        }}
      >
        <div className='groupWrapper'>
          {config?.appearance?.showMenuGroupTitle !== false && (
            <div className='groupDetails'>
              <div className='groupName'>
                <h2>{name}</h2>
              </div>
              <div className='groupDescription'>{description}</div>
            </div>
          )}
          <div className={classnames('groupItems', columnsClass)}>
            {itemsToRender
              .filter((item) => !!item)
              .map((item: GroupItemBackupContent | DoMenusGetMenuItem) => {
                return (
                  <div style={dividerStyle} key={item.guid}>
                    <LinkedItem item={item} {...props} />
                  </div>
                )
              })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default GroupedMenuItems
